import { FormLabel, OptionType, SearchableSelect } from "shared";
import {
    ComponentTypes,
    ITagSelectComponent,
    ITemplateAutonumberComponent,
    ITemplateComponent,
    ITemplateCounterpartyComponent,
    ITemplateHrPersonComponent,
    ITemplateYandexComponent
} from "entities/template";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TComponentsTranslationPaths, useComponentsTranslations } from "../../../../lib/i18n";
import { YandexWordFields } from "../../../../lib/const/yandex_word_fields";
import { LegalYandexWordFields } from "../../../../lib/const/legal_yandex_word_fields";
import { VendorFields } from "../../../../lib/const/vendor_fields";
import { EmployeeWordFields } from "../../../../lib/const/employee_word_fields";
import { TAG_PREFIX } from "../../../../lib/const/const";
import { isTagSelectComponent } from "../../../../lib/utils/service-component-rules";

interface ITagSelectFieldProps {
    id?: string;
    value?: string;
    forceEmpty?: boolean;
    /** Если true — для гк Яндекс / контрагента / сотрудника в списке все поля типа (включая id), а не только fieldsList компонента. */
    fullList?: boolean;
    /** Разрешает теги порядковых номеров для интеграции с Трекером, но не для условий видимости. */
    includeAutonumber?: boolean;
    placeholder?: string;
    title?: string;
    componentList: ITemplateComponent[];
    currentComponent: ITemplateComponent;
    onChange: (value?: string, componentId?: number) => void;

}

export const TagSelectField: React.FC<ITagSelectFieldProps> = React.memo(({
                                                                              id,
                                                                              value,
                                                                              componentList,
                                                                              currentComponent,
                                                                              placeholder,
                                                                              title,
                                                                              onChange,
                                                                              forceEmpty = false,
                                                                              fullList = false,
                                                                              includeAutonumber = false,
                                                                          }) => {
    const t = useComponentsTranslations();
    const { i18n } = useTranslation();
    const [searchableSelectOptions, setSearchableSelectOptions] = useState<OptionType[]>([]);

    const legalYandexGroupOptions = (components: ITemplateComponent[]): OptionType | undefined => {
        const legalComponents = components.filter(
            (c) => c.type === ComponentTypes.legalYandex && currentComponent.id !== c.id
        ) as ITemplateYandexComponent[];

        const constructOptions = (component: ITemplateYandexComponent) => {
            const keys = fullList
                ? (Object.values(LegalYandexWordFields).filter((key) => key !== LegalYandexWordFields.ya_is_beyond) as string[])
                : component.fieldsList;
            if (!keys?.length) {
                return [];
            }
            return keys.map((key) => {
                const keyWithSuffix = `${key}${component.suffix ? component.suffix : ""}`;
                return {
                    value: keyWithSuffix,
                    id: component.id,
                    label:
                        t(`legalYandexFields.${key as LegalYandexWordFields}` as TComponentsTranslationPaths)
                        + ` (${keyWithSuffix})`,
                };
            });
        };

        if (legalComponents.length > 0) {
            let fullOptions: OptionType[] = [];
            legalComponents.forEach((component) => {
                fullOptions = [...fullOptions, ...constructOptions(component)];
            });
            return {
                label: t("legalYandexLabel"),
                grouped: true,
                value: "",
                options: fullOptions,
            };
        }
    };

    const yandexOebsGroupOptions = (components: ITemplateComponent[]): OptionType | undefined => {
        const yandexComponents = components.filter(
            (c) => c.type === ComponentTypes.yandex && currentComponent.id !== c.id
        ) as ITemplateYandexComponent[];

        const constructOptions = (component: ITemplateYandexComponent) => {
            const keys = fullList
                ? (Object.values(YandexWordFields) as string[])
                : component.fieldsList;
            if (!keys?.length) {
                return [];
            }
            return keys.map((key) => {
                const keyWithSuffix = `${key}${component.suffix ? component.suffix : ""}`;
                return {
                    value: keyWithSuffix,
                    id: component.id,
                    label:
                        t(`yandexFields.${key as YandexWordFields}` as TComponentsTranslationPaths)
                        + ` (${keyWithSuffix})`,
                };
            });
        };

        if (yandexComponents.length > 0) {
            let fullOptions: OptionType[] = [];
            yandexComponents.forEach((component) => {
                fullOptions = [...fullOptions, ...constructOptions(component)];
            });
            return {
                label: t("yandexLabel"),
                grouped: true,
                value: "",
                options: fullOptions,
            };
        }
    };

    const counterPartyOptions = (components: ITemplateComponent[]): OptionType | undefined => {
        const counterpartyComponents = components.filter((c) => [
            ComponentTypes.counterparty,
        ].includes(c.type) && currentComponent.id !== c.id) as ITemplateCounterpartyComponent[];

        const constructCounterpartyOptions = (component: ITemplateCounterpartyComponent) => {
            const keys = fullList ? (Object.values(VendorFields) as string[]) : component.fieldsList;
            if (!keys?.length) {
                return [];
            }
            return keys.map((key) => {
                const keyWithSuffix = `${key}${component.suffix ? component.suffix : ""}`;
                return {
                    value: keyWithSuffix,
                    id: component.id,
                    label: t(`vendorFields.${key as VendorFields}`) + ` (${keyWithSuffix})`
                };
            });
        };

        if (counterpartyComponents.length > 0) {
            let fullOptions: OptionType[] = [];
            counterpartyComponents.forEach((component) => {
                const options = constructCounterpartyOptions(component);
                fullOptions = [...fullOptions, ...options];
            });
            return {
                label: t("counterpartyLabel"),
                grouped: true,
                value: "",
                options: fullOptions
            };
        }
    };

    const hrOptions = (components: ITemplateComponent[]): OptionType | undefined => {
        const hrComponents = components.filter((c) => [
            ComponentTypes.hrPerson,
        ].includes(c.type) && currentComponent.id !== c.id) as ITemplateHrPersonComponent[];

        const constructHrOptions = (component: ITemplateHrPersonComponent) => {
            const keys = fullList ? (Object.values(EmployeeWordFields) as string[]) : component.fieldsList;
            if (!keys?.length) {
                return [];
            }
            return keys.map((key) => {
                const keyWithSuffix = `${key}${component.suffix ? component.suffix : ""}`;
                return {
                    value: keyWithSuffix,
                    id: component.id,
                    label: t(`hrFields.${key as EmployeeWordFields}`) + ` (${keyWithSuffix})`
                };
            });
        };

        if (hrComponents.length > 0) {
            let fullOptions: OptionType[] = [];
            hrComponents.forEach((component) => {
                const options = constructHrOptions(component);
                fullOptions = [...fullOptions, ...options];
            });
            return {
                label: t("employeeLabel"),
                grouped: true,
                value: "",
                options: fullOptions
            };
        }
    };

    useEffect(() => {
        let tagList: OptionType[];
        const tagComponentList = componentList.filter((c) =>
            (isTagSelectComponent(c.type) || (includeAutonumber && c.type === ComponentTypes.autonumber))
            && currentComponent.id !== c.id
        ) as (ITagSelectComponent | ITemplateAutonumberComponent)[];

        const templateTagOptions = [{
            label: `${t("docName")} (docName)`,
            value: "docName"
        }, {
            label: `${t("docCode")} (docCode)`,
            value: "docCode"
        }];

        const getComponentTag = (c: ITagSelectComponent | ITemplateAutonumberComponent) => {
            if (c.type === ComponentTypes.money) {
                return TAG_PREFIX + c.name;
            }
            return c.name;
        };

        const componentOptions: OptionType[] = tagComponentList
            .filter(c => c.name)
            .map((c) => {
                const label = c.type === ComponentTypes.autonumber
                    ? (c as ITemplateAutonumberComponent).counterName
                    : c.label;
                return {
                    label: (label ? `${label} (${c.name})` : c.name) as string,
                    id: c.id,
                    value: getComponentTag(c)
                };
            });

        tagList = ([...componentOptions, ...templateTagOptions]);

        const legalYandexOption = legalYandexGroupOptions(componentList);
        if (legalYandexOption) {
            tagList.push(legalYandexOption);
        }
        const yandexOebsOption = yandexOebsGroupOptions(componentList);
        if (yandexOebsOption) {
            tagList.push(yandexOebsOption);
        }
        const counterpartyOpts = counterPartyOptions(componentList);
        if (counterpartyOpts) {
            tagList.push(counterpartyOpts);
        }

        const hrOpts = hrOptions(componentList);
        if (hrOpts) {
            tagList.push(hrOpts);
        }

        setSearchableSelectOptions(tagList);
    }, [i18n.language, componentList, fullList, includeAutonumber, currentComponent.id]);

    const getOptionValue = (value: string | undefined): OptionType | undefined => {
        if (!value || forceEmpty) {
            return undefined;
        }
        // пытаемся найти опцию в линейной структуре
        const option = searchableSelectOptions.find((o) => o.value === value && !o.grouped) as OptionType | undefined;
        if (option) {
            return option;
        }
        //иначе в сгруппированных списках
        const gropedOptions = searchableSelectOptions.filter((o) => o.grouped).map(o => o.options).flat(1);
        return gropedOptions.find((o) => o.value === value) as OptionType | undefined;
    };

    return <FormLabel title={title}>
        <SearchableSelect
            id={id}
            options={searchableSelectOptions}
            placeholder={placeholder}
            displayValueInSelect={!forceEmpty}
            optionValue={getOptionValue(value)}
            onOptionChange={(v) => {
                const option = v as OptionType | undefined;
                if (option) {
                    onChange(option.value, option.id);
                } else {
                    onChange(undefined);
                }
            }}
            isMulti={false}
            hideClearIcon={false}
            isSearchable={true}
        />
    </FormLabel>;

});
